import React, { useState, useEffect, useCallback } from 'react';
import {
  downloadCrmDashboardComments,
  getCrmDashboard,
  getCrmDashboardHubs,
} from '../config/api';

// Seconds → compact talk-time label: "10m", "1h 05m", "45s".
const formatDuration = (seconds) => {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  if (s === 0) return '—';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, '0')}s`;
  return `${sec}s`;
};

const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultDateRange = () => {
  const today = new Date();
  const targetMonthLastDay = new Date(today.getFullYear(), today.getMonth(), 0).getDate();
  const oneMonthAgo = new Date(
    today.getFullYear(),
    today.getMonth() - 1,
    Math.min(today.getDate(), targetMonthLastDay)
  );
  return { from: toDateInputValue(oneMonthAgo), to: toDateInputValue(today) };
};

const formatDateLabel = (value) => {
  const [year, month, day] = String(value || '').split('-').map(Number);
  if (!year || !month || !day) return '';
  return new Date(year, month - 1, day).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

// KPI tiles, in display order. `accent` drives the left color bar.
const TILE_DEFS = [
  { key: 'callsMade', label: 'Calls made', accent: 'green' },
  { key: 'connected', label: 'Connected', accent: 'teal' },
  { key: 'uniqueLeadsConnected', label: 'Unique leads connected', accent: 'blue' },
  { key: 'firstOrders', label: 'First orders', accent: 'purple' },
  { key: 'activeLeads', label: 'Active leads', accent: 'amber' },
  { key: 'updatedLeads', label: 'Leads updated', accent: 'indigo' },
  { key: 'followUps', label: 'Follow-ups', accent: 'red' },
];

// Simple grouped bar chart (calls vs connected) per hub, drawn with SVG so no
// charting dependency is needed.
const CallsByHubChart = ({ data }) => {
  if (!data || data.length === 0) {
    return <div className="crm-dash-empty">No call activity mapped to hubs in this period.</div>;
  }
  const max = Math.max(1, ...data.map((d) => d.calls));
  const barMaxH = 150;
  const groupW = 62;
  const chartH = barMaxH + 34;
  const width = Math.max(data.length * groupW, 320);

  return (
    <div className="crm-dash-chart-scroll">
      <svg width={width} height={chartH} role="img" aria-label="Calls by hub">
        {data.map((d, i) => {
          const x = i * groupW + 12;
          const callsH = Math.round((d.calls / max) * barMaxH);
          const connH = Math.round((d.connected / max) * barMaxH);
          const baseY = barMaxH + 6;
          const chartLabel = d.hubType === 'VIRTUAL' ? d.hubOwnName : d.hubName;
          return (
            <g key={d.hubId}>
              <title>{d.hubName}</title>
              {/* total calls (light) */}
              <rect x={x} y={baseY - callsH} width="20" height={callsH} rx="3" fill="#d7e3dd" />
              {/* connected (green) */}
              <rect x={x + 22} y={baseY - connH} width="20" height={connH} rx="3" fill="#1f9d57" />
              <text x={x + 21} y={baseY - callsH - 5} textAnchor="middle" className="crm-dash-bar-val">
                {d.calls}
              </text>
              <text x={x + 21} y={chartH - 8} textAnchor="middle" className="crm-dash-bar-label">
                {(chartLabel || '').replace(/ Hub$/i, '').slice(0, 10)}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="crm-dash-legend">
        <span><i className="crm-dash-swatch crm-dash-swatch-calls" /> Calls</span>
        <span><i className="crm-dash-swatch crm-dash-swatch-conn" /> Connected</span>
      </div>
    </div>
  );
};

const DashboardSkeleton = () => (
  <div className="crm-dash-skeleton" aria-label="Loading dashboard" aria-busy="true">
    <div className="crm-dash-tiles">
      {TILE_DEFS.map((tile) => (
        <div className="crm-dash-tile crm-dash-skeleton-tile" key={tile.key}>
          <span className="crm-dash-skeleton-line crm-dash-skeleton-label" />
          <span className="crm-dash-skeleton-line crm-dash-skeleton-value" />
          <span className="crm-dash-skeleton-line crm-dash-skeleton-note" />
        </div>
      ))}
    </div>
    <div className="crm-dash-card crm-dash-skeleton-card">
      <span className="crm-dash-skeleton-line crm-dash-skeleton-heading" />
      <div className="crm-dash-skeleton-chart">
        {[42, 68, 50, 82, 58, 74, 46, 64].map((height, index) => (
          <span key={index} style={{ height: `${height}%` }} />
        ))}
      </div>
    </div>
    {[0, 1].map((section) => (
      <div className="crm-dash-card crm-dash-skeleton-card" key={section}>
        <span className="crm-dash-skeleton-line crm-dash-skeleton-heading" />
        <div className="crm-dash-skeleton-table">
          {[0, 1, 2, 3].map((row) => <span key={row} />)}
        </div>
      </div>
    ))}
  </div>
);

const CrmDashboard = () => {
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [hubId, setHubId] = useState('');
  const [omsMatched, setOmsMatched] = useState('');
  const [hubs, setHubs] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [agentSearchInput, setAgentSearchInput] = useState('');
  const [agentSearch, setAgentSearch] = useState('');
  const [agentPage, setAgentPage] = useState(1);
  const [agentLimit, setAgentLimit] = useState(10);
  const [exportingComments, setExportingComments] = useState(false);

  useEffect(() => {
    getCrmDashboardHubs()
      .then((res) => setHubs((res?.data || res)?.hubs || []))
      .catch(() => setHubs([]));
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setAgentPage(1);
      setAgentSearch(agentSearchInput.trim());
    }, 300);
    return () => clearTimeout(timeout);
  }, [agentSearchInput]);

  const load = useCallback(async () => {
    if (!dateRange.from || !dateRange.to || dateRange.from > dateRange.to) {
      setError('Select a valid date range. Start date must be on or before end date.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await getCrmDashboard({
        from: dateRange.from,
        to: dateRange.to,
        hubId,
        omsMatched,
        agentSearch,
        agentPage,
        agentLimit,
      });
      setData(res?.data || res);
    } catch (err) {
      setError(err.message || 'Could not load the dashboard.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dateRange.from, dateRange.to, hubId, omsMatched, agentSearch, agentPage, agentLimit]);

  useEffect(() => { load(); }, [load]);

  const downloadComments = async () => {
    if (!dateRange.from || !dateRange.to || dateRange.from > dateRange.to) {
      setError('Select a valid date range before downloading comments.');
      return;
    }
    setExportingComments(true);
    setError('');
    try {
      await downloadCrmDashboardComments({
        from: dateRange.from,
        to: dateRange.to,
        hubId,
        omsMatched,
      });
    } catch (err) {
      setError(err.message || 'Could not download comments.');
    } finally {
      setExportingComments(false);
    }
  };

  const tiles = data?.tiles || {};
  const byUsers = data?.byUser || [];
  const agentPagination = data?.byUserPagination || {
    page: 1,
    limit: agentLimit,
    total: byUsers.length,
    totalPages: byUsers.length > 0 ? 1 : 0,
  };
  const rangeLabel = `${formatDateLabel(dateRange.from)} – ${formatDateLabel(dateRange.to)}`;
  const firstAgentRow = agentPagination.total > 0
    ? (agentPagination.page - 1) * agentPagination.limit + 1
    : 0;
  const lastAgentRow = Math.min(agentPagination.page * agentPagination.limit, agentPagination.total);

  return (
    <div className="container crm-dash">
      <div className="crm-dash-header">
        <div>
          <h1 className="crm-dash-title">Dashboard</h1>
          <p className="crm-dash-subtitle">CRM performance and activity overview</p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={downloadComments}
          disabled={exportingComments}
        >
          {exportingComments ? 'Downloading…' : 'Export comments'}
        </button>
      </div>

      <div className="crm-dash-filter-panel">
        <div className="crm-dash-filter-intro">
          <span className="crm-dash-filter-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <path d="M7 3v3M17 3v3M4 9h16M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z" />
            </svg>
          </span>
          <div>
            <div className="crm-dash-filter-title">Reporting period</div>
            <div className="crm-dash-filter-summary">{rangeLabel} · CRM-initiated calls</div>
          </div>
          {loading && data && <span className="crm-dash-filter-loading">Updating…</span>}
        </div>

        <div className="crm-dash-filters">
          <label className="crm-dash-filter-control">
            <span>From</span>
            <input type="date" className="form-control" value={dateRange.from} max={dateRange.to}
              onChange={(e) => {
                setAgentPage(1);
                setDateRange((current) => ({ ...current, from: e.target.value }));
              }} />
          </label>
          <span className="crm-dash-date-separator" aria-hidden="true">→</span>
          <label className="crm-dash-filter-control">
            <span>To</span>
            <input type="date" className="form-control" value={dateRange.to} min={dateRange.from}
              onChange={(e) => {
                setAgentPage(1);
                setDateRange((current) => ({ ...current, to: e.target.value }));
              }} />
          </label>

          <label className="crm-dash-filter-control crm-dash-hub-filter">
            <span>Hub</span>
            <select className="form-control" value={hubId} onChange={(e) => {
              setAgentPage(1);
              setHubId(e.target.value);
            }}>
              <option value="">All hubs</option>
              {hubs.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
            </select>
          </label>

          <label className="crm-dash-filter-control crm-dash-app-user-filter">
            <span>App user</span>
            <select className="form-control" value={omsMatched} onChange={(e) => {
              setAgentPage(1);
              setOmsMatched(e.target.value);
            }}>
              <option value="">All</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </label>
        </div>
      </div>

      {error && <div className="crm-form-error" style={{ margin: '12px 0' }}>{error}</div>}
      {loading && <DashboardSkeleton />}

      {!loading && data && (
        <>
          {/* KPI tiles */}
          <div className="crm-dash-tiles">
            {TILE_DEFS.map((def) => {
              const t = tiles[def.key] || { value: 0, note: '' };
              return (
                <div className={`crm-dash-tile crm-dash-tile-${def.accent}`} key={def.key}>
                  <div className="crm-dash-tile-label">{def.label}</div>
                  <div className="crm-dash-tile-value">{Number(t.value).toLocaleString('en-IN')}</div>
                  <div className="crm-dash-tile-note">{t.note}</div>
                </div>
              );
            })}
          </div>

          {/* Calls by hub chart (full width) */}
          <div className="crm-dash-card">
            <div className="crm-dash-card-title">Calls by hub · {rangeLabel}</div>
            <CallsByHubChart data={data.callsByHub} />
          </div>

          {/* By agent table (full width — includes talk time) */}
          <div>
            <div className="crm-dash-card">
              <div className="crm-dash-card-heading">
                <div className="crm-dash-card-title">By Agent</div>
                <div className="crm-dash-agent-tools">
                  <input type="search" className="form-control" value={agentSearchInput}
                    onChange={(e) => setAgentSearchInput(e.target.value)}
                    placeholder="Search agent" aria-label="Search agents" />
                  <select className="form-control" value={agentLimit} aria-label="Agents per page"
                    onChange={(e) => {
                      setAgentPage(1);
                      setAgentLimit(Number(e.target.value));
                    }}>
                    <option value={10}>10 per page</option>
                    <option value={25}>25 per page</option>
                    <option value={50}>50 per page</option>
                  </select>
                </div>
              </div>
              <div className="crm-dash-table-wrap">
                <table className="crm-dash-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th className="num">Calls</th>
                      <th className="num">Conn.</th>
                      <th className="num">Leads</th>
                      <th className="num">Total talk time</th>
                      <th className="num">Talked today</th>
                      <th className="num">Avg / day</th>
                      <th className="num">Overdue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byUsers.length === 0 && (
                      <tr><td colSpan={8} className="crm-dash-empty-cell">
                        {agentSearch ? 'No agents match this search.' : 'No calls in this period.'}
                      </td></tr>
                    )}
                    {byUsers.map((u) => (
                      <tr key={u.userId}>
                        <td>{u.userName || `User ${u.userId}`}</td>
                        <td className="num">{u.calls}</td>
                        <td className="num">{u.connected}</td>
                        <td className="num">{u.leads}</td>
                        <td className="num crm-dash-dur">{formatDuration(u.totalSeconds)}</td>
                        <td className="num crm-dash-dur-today">{formatDuration(u.todaySeconds)}</td>
                        <td className="num crm-dash-dur-soft">{formatDuration(u.avgPerDaySeconds)}</td>
                        <td className={`num${u.overdue > 0 ? ' crm-dash-warn' : ''}`}>{u.overdue}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="crm-dash-agent-pagination">
                <span>
                  {agentPagination.total > 0
                    ? `${firstAgentRow}–${lastAgentRow} of ${agentPagination.total}`
                    : '0 agents'}
                </span>
                <div>
                  <button type="button" disabled={loading || agentPagination.page <= 1}
                    onClick={() => setAgentPage((page) => Math.max(1, page - 1))}>
                    Previous
                  </button>
                  <span>Page {agentPagination.page} of {Math.max(agentPagination.totalPages, 1)}</span>
                  <button type="button" disabled={loading || agentPagination.page >= agentPagination.totalPages}
                    onClick={() => setAgentPage((page) => page + 1)}>
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* By hub table */}
          <div className="crm-dash-card">
            <div className="crm-dash-card-title">By hub</div>
            <div className="crm-dash-table-wrap">
              <table className="crm-dash-table">
                <thead>
                  <tr>
                    <th>Hub</th>
                    <th className="num">Leads linked</th>
                    <th className="num">Active leads</th>
                    <th className="num">Calls in range</th>
                    <th className="num">Connected</th>
                    <th className="num">Connect rate</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byHub.length === 0 && (
                    <tr><td colSpan={6} className="crm-dash-empty-cell">No hub data.</td></tr>
                  )}
                  {data.byHub.map((h) => (
                    <tr key={h.hubId}>
                      <td>{h.hubName}</td>
                      <td className="num">{h.leadsLinked}</td>
                      <td className="num">{h.activeLeads}</td>
                      <td className="num">{h.calls}</td>
                      <td className="num">{h.connected}</td>
                      <td className="num">
                        <span className="crm-dash-rate">{h.connectRate}%</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CrmDashboard;
